import { useCallback, useEffect, useRef, useState } from "react";
import { ZIP_LOOKUP_URL } from "../config/config";

// SP-11. The one place the app looks a postcode up.
//
// Every address form used to do this inline with a bare fetch: no time limit, no way to cancel,
// and an error that was recorded into state nobody rendered. When the service was slow the
// country/state/city fields simply never filled in and nothing on screen said why; when it failed
// the user saw three empty fields and could still save with the previous address in place.
//
// What this adds over the inline version:
//   * a time limit (LOOKUP_TIMEOUT_MS) enforced with AbortController, so a hung request ends
//   * cancellation of the in-flight request when a newer postcode is typed, so a slow first
//     response can never land after a faster second one and overwrite it
//   * cancellation on unmount, so no setState after the form has gone
//   * `loading`, so the form can say something is happening
//   * `error`, which the caller is expected to RENDER next to the postcode field
//
// The lookup still goes to a third party straight from the browser. Moving it behind our own
// endpoint - so the user's postcode never leaves the browser to anyone else, and so an audit of
// outbound calls can see it - is the follow-up the ticket calls "better still"; Common/Countries,
// Common/States/{id} and Common/Cities/{id} already exist to build on.
const LOOKUP_TIMEOUT_MS = 8000;

// The service answers on a 5-digit US postcode. Anything shorter is someone still typing.
export const ZIP_LENGTH = 5;

export const useZipLookup = () => {
  const [location, setLocation] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // One controller per hook instance: starting a lookup aborts whatever was still running.
  const controllerRef = useRef(null);
  // Guards every setState below, so an abort that loses the race cannot write after unmount.
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
    };
  }, []);

  const reset = useCallback(() => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
    if (!mountedRef.current) return;
    setLocation(null);
    setError("");
    setLoading(false);
  }, []);

  const lookup = useCallback(async (zipCode) => {
    if (!zipCode || String(zipCode).length !== ZIP_LENGTH) {
      return null;
    }

    // Without this the browser resolves `undefined12345` against our own origin and the failure
    // looks like "postcode not found". REACT_APP_ZIP_LOOKUP_URL is commented out in .env.example,
    // so a fresh clone hits exactly this path.
    if (!ZIP_LOOKUP_URL) {
      if (mountedRef.current) {
        setLocation(null);
        setLoading(false);
        setError(
          "Address lookup is not configured. Enter your country, state and city manually."
        );
      }
      return null;
    }

    if (controllerRef.current) {
      controllerRef.current.abort();
    }
    const controller = new AbortController();
    controllerRef.current = controller;
    const timeoutId = setTimeout(() => controller.abort(), LOOKUP_TIMEOUT_MS);

    if (mountedRef.current) {
      setLoading(true);
      setError("");
    }

    try {
      const response = await fetch(`${ZIP_LOOKUP_URL}${zipCode}`, {
        signal: controller.signal,
      });
      if (!response.ok) {
        throw new Error("Zip code not found");
      }
      const data = await response.json();
      if (!mountedRef.current || controller.signal.aborted) return null;
      setLocation(data);
      setError("");
      return data;
    } catch (err) {
      // A newer lookup aborting this one is not a failure the user should ever see - the newer
      // one owns the state now.
      if (controller.signal.aborted && controllerRef.current !== controller) {
        return null;
      }
      if (!mountedRef.current) return null;
      setLocation(null);
      setError(
        err?.name === "AbortError"
          ? "Address lookup timed out. Enter your country, state and city manually."
          : err?.message === "Zip code not found"
          ? "We could not find that zip code. Check it, or enter your address manually."
          : "Address lookup is unavailable. Enter your country, state and city manually."
      );
      return null;
    } finally {
      clearTimeout(timeoutId);
      if (controllerRef.current === controller) {
        controllerRef.current = null;
        if (mountedRef.current) setLoading(false);
      }
    }
  }, []);

  return { location, error, loading, lookup, reset };
};

export default useZipLookup;
